"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getImpliedScopeLevels = exports.getImpliedScopes = exports.isPermissionImpliedInUserScope = exports.hasPermissions = void 0;
const permissions_constant_1 = require("../constants/permissions.constant");
const scopeLevels = { user: 1, admin: 2, super: 3 };
/**
 * @function hasPermissions
 * @description validates the user has the required permissions for a specific route
 * @param userAppPermissions
 * @param userScopes
 * @param permissionRequirements
 * @returns {Boolean}
 */
const hasPermissions = (userAppPermissions = [], userScopes = [], permissionRequirements) => {
    const { permissions = [], scopes = [], options = { scopeMode: 'OR', permissionMode: 'OR' } } = permissionRequirements;
    if (userScopes.includes(permissions_constant_1.SERVICE_ACCOUNT) || userScopes.includes(permissions_constant_1.TE_ROOT_USER)) {
        return true;
    }
    /**
     * Route permission was defined wrongly.
     * Since we don't know which one of these is wrong just return false
     */
    if (scopes.length && permissions.length)
        return false;
    if (!permissions.length && !scopes.length)
        return userScopes.length > 0;
    if (permissions.length) {
        const userHasPermissionOrScope = (permission) => {
            const isPermissionMatched = userAppPermissions.indexOf(permission) > -1;
            const isPermissionImplied = (0, exports.isPermissionImpliedInUserScope)(userScopes, permission, permissions_constant_1.adminLevelAppPermissions.indexOf(permission) > -1 ? ['admin', 'super'] : ['super']);
            return isPermissionMatched || isPermissionImplied;
        };
        if (options.permissionMode === 'AND') {
            return permissions.every(userHasPermissionOrScope);
        }
        return permissions.some(userHasPermissionOrScope);
    }
    /**
     * A user has to have either scope level access or app permission access!
     * Check scope permissions
     */
    if (options.scopeMode === 'AND') {
        const allScopesMatch = scopes.every((scope) => getIfUserHasScopePermission(userScopes, scope));
        return allScopesMatch;
    }
    const someScopeMatch = scopes.some((scope) => getIfUserHasScopePermission(userScopes, scope));
    return someScopeMatch;
};
exports.hasPermissions = hasPermissions;
/**
 * Check if a user has the same or higher scope.
 * @param userScopes
 * @param scope
 */
const getIfUserHasScopePermission = (userScopes, scope) => {
    if (userScopes.indexOf(scope) > -1)
        return true;
    // Scope not found with exact match, need to validate if user has a higher scope
    const [scopeAppId, scopeLevel] = scope.split('::');
    return userScopes.some((userScope) => {
        const [userScopeAppId, userScopeLevel] = userScope.split('::');
        if (userScopeAppId === scopeAppId)
            // Only compare with scopes that have the same appId
            return scopeLevels[userScopeLevel] > scopeLevels[scopeLevel];
        return false;
    });
};
/**
 * Check if the permission is implied in any of the user scopes.
 * @param userScopes
 * @param permission
 * @param allowedScopes
 */
const isPermissionImpliedInUserScope = (userScopes, permission, allowedScopes = ['super']) => {
    if (userScopes.includes(permissions_constant_1.SERVICE_ACCOUNT) || userScopes.includes(permissions_constant_1.TE_ROOT_USER))
        return true;
    return userScopes.some((scope) => {
        const [scopeAppId, scopeRule] = scope.split('::');
        return scopeAppId === permission.split('::')[0] && allowedScopes.includes(scopeRule);
    });
};
exports.isPermissionImpliedInUserScope = isPermissionImpliedInUserScope;
/**
 * Given a scope find all implied scopes (including the original scope)
 * For example: TE_ADMIN::admin will imply TE_ADMIN::admin and TE_ADMIN::user
 * @param scope
 * @returns {String[]}
 */
const getImpliedScopes = (scope) => {
    const [scopeApp, scopeLevel] = scope.split('::');
    const impliedScopeLevels = (0, exports.getImpliedScopeLevels)(scopeLevel);
    const impliedScopes = impliedScopeLevels.map((level) => {
        return `${scopeApp}::${level}`;
    });
    return impliedScopes;
};
exports.getImpliedScopes = getImpliedScopes;
/**
 * Given a scope level find all implied scopes levels (including the original scope level)
 * For example: admin will imply admin and user
 * @param scopeLevel
 * @returns {String[]}
 */
const getImpliedScopeLevels = (scopeLevel) => {
    // current scope level and all implied levels below (according to scopeLevels, if key does not match it will be ignored)
    const impliedScopeLevels = Object.keys(scopeLevels).filter((level) => scopeLevels[scopeLevel] >= scopeLevels[level]);
    return impliedScopeLevels;
};
exports.getImpliedScopeLevels = getImpliedScopeLevels;
